import { addMonths, format, parseISO, subMonths } from 'date-fns';
import React from 'react';
import { AgeCategory, BrandCode, Session, Volume } from '../../curriculum';
import { OverviewEventsSection } from '../components/OverviewEventsSection';
import { AdvancePlanningSection } from './advancePlanning';

export type OverviewContentSection<C extends React.ComponentType<any> = React.ComponentType<any>> = {
  location: 'main' | 'sidebar';
  heading: string;
  viewAllUrl?: string;
  component: C;
  props: React.ComponentProps<C>;
};

export type OverviewContent = {
  title: string;
  navLabels: [string, string];
  volume: Volume<AdvancePlanningSection>;
  sessions: Session[];
  planningItems: AdvancePlanningSection[];
  additionalSections: OverviewContentSection[];
};

type OverviewContentTransformer = (
  brandCode: BrandCode,
  ageCategory: AgeCategory,
  scheduleVolumes: Volume[],
  advancePlanningVolumes: Volume<AdvancePlanningSection>[],
  sessions: Session[]
) => OverviewContent[];

export const transformHyfiOverviewContent: OverviewContentTransformer = (
  brandCode,
  ageCategory,
  scheduleVolumes,
  advancePlanningVolumes,
  sessions
) => {
  const additionalSections: OverviewContentSection[] = [];

  const familyEvent = advancePlanningVolumes.find(v => v.name.toLowerCase().includes('family and volunteer events'));

  const isSessionDeactivated = (session: Session) => !session.timelines?.length;

  if (familyEvent && familyEvent.sections.length > 0) {
    additionalSections.push({
      location: 'main',
      heading: 'Family and Volunteer Events',
      viewAllUrl: `/manage-bible-study/advance-planning/${brandCode}/${ageCategory}/${familyEvent.curriculumId}?volumeId=${familyEvent._id}`,
      component: OverviewEventsSection,
      props: {
        brandCode,
        ageCategory,
        curriculumId: familyEvent.curriculumId,
        events: familyEvent.sections.slice(0, 2),
        image: familyEvent.imageUrl,
      },
    } satisfies OverviewContentSection<typeof OverviewEventsSection>);
  }

  return scheduleVolumes.map(sv => {
    const date = parseISO(sv.startDate);
    const volume =
      advancePlanningVolumes.find(av => av._id === sv._id) || (sv as unknown as Volume<AdvancePlanningSection>);

    return {
      title: format(date, 'MMMM yyyy'),
      navLabels: [format(subMonths(date, 1), 'MMM'), format(addMonths(date, 1), 'MMM')],
      volume: volume,
      sessions: sessions.filter(session => !isSessionDeactivated(session) && session.volumeId === volume._id),
      planningItems: volume.sections || [],
      additionalSections,
    };
  });
};
